import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Stack,
  Shelf,
  Card,
  Text,
  Progress,
  Toggle,
  Button,
  Banner,
  Popover,
  Position,
} from "@chaser/components";
import { FileText, Gift, UserPlus, Users } from "lucide-react";
import { useLocation } from "react-router";
import FullLayoutLoader from "../../../../components/FullLayoutLoader";
import SkeletonLayout from "../../../../components/SkeletonLayout";
import AddAddonModal from "../../../../components/Addon/AddAddonModal";
import ModuleSummaryCard from "./components/ModuleSummaryCard";
import ComparePlansPopover from "./components/ComparePlansPopover";
import PlanComparisonGrid from "./components/PlanComparisonGrid";
import AddonRowsSection from "./components/AddonRowsSection";
import IncludedByPlanTable from "./components/IncludedByPlanTable";
import useAddonsState from "./useAddonsState";
import useModuleStatuses from "./useModuleStatuses";
import DowngradeModal from "../Subscriptions/PlanBox/DowngradeModal";
import useModuleInfo from "../../../../hooks/useModuleInfo";
import useOrganisationUtils from "../../../../hooks/useOrganisationUtils";
import useModal from "../../../../hooks/useModal";
import { addonIds, BillingPeriods } from "../../../../util/constants";
import { DEFAULT_RAIL_SECTION, ORGANISATION_MODULE_ID } from "./constants";
import {
  buildAddonRowSignature,
  findAddonRowById,
  getDefaultRailSection,
  getModuleTagLabel,
  getSelectedModuleFromSearch,
  isConfiguredOrganisationAddon,
  isScheduledForRemoval,
  normalizePendingAddonId,
} from "./viewHelpers";

import styles from "./Addons.module.scss";

const Addons = () => {
  const [selectedRailSection, setSelectedRailSection] =
    useState(DEFAULT_RAIL_SECTION);
  const [includedByPlanOpenId, setIncludedByPlanOpenId] = useState(null);
  const [cancellingOrganisationAddonId, setCancellingOrganisationAddonId] =
    useState(null);
  const [pendingAddonState, setPendingAddonState] = useState(null);
  const hasBootstrappedOrgDeepLinkRef = useRef(false);
  const showModal = useModal();
  const location = useLocation();
  const selectedModuleFromQuery = getSelectedModuleFromSearch(location.search);
  const { formatCurrency } = useOrganisationUtils();
  const {
    refetchSubscriptionData,
    currencyCode,
    plan,
    loading: loadingSubscription,
  } = useModuleInfo(undefined, { suspense: false });
  const {
    activeAddonId,
    setActiveAddonId,
    showComparePlans,
    setShowComparePlans,
    setTargetPlanName,
    isInitialLoading,
    isModuleSwitching,
    moduleSubscriptions,
    availableModules,
    selectedModule,
    selectedModuleSupportsAddons,
    selectedModuleAmountMinor,
    planAmountMinor,
    moduleTotalCurrentCadenceMinor,
    moduleTotalAnnualEquivalentMinor,
    activeModuleCount,
    selectedPlanRevenueText,
    visiblePlans,
    compareCurrentPlanAmount,
    selectedOfferPeriod,
    setSelectedOfferPeriod,
    isSelectedModuleOfferFlow,
    isSelectedModuleInTrial,
    isSelectedModuleExpiredTrial,
    selectedModuleTrialEndsAt,
    isStartingModuleTrial,
    startSelectedModuleTrial,
    credits,
    additionalFeatures,
    organisationAddonRows,
    refetchOrganisationAddons,
    refetchSelectedModuleAddons,
    onActionClick,
    onRemoveClick,
    removingAddonId,
    onCancelScheduledChange,
    isLoadingCancelScheduled,
    selectModuleById,
  } = useAddonsState(selectedModuleFromQuery);
  const { canPurchaseOrganisationAddons } =
    useModuleStatuses(moduleSubscriptions);
  const pendingAddonId = pendingAddonState?.addonId || null;

  const selectedPlanPeriod =
    selectedModule?.period ||
    selectedModule?.plan?.period ||
    plan?.period ||
    BillingPeriods.MONTHLY;
  const selectedPlanRecurringSuffix =
    selectedPlanPeriod === BillingPeriods.YEARLY ? " /yr" : " /mo";
  const comparisonRecurringSuffix =
    selectedOfferPeriod === BillingPeriods.YEARLY ? " /yr" : " /mo";
  const currentPlanAmount = planAmountMinor / 100;
  const hasSelectedModuleActivePlan =
    Boolean(selectedModule?.isActive) && Boolean(selectedModule?.planName);
  const canComparePlans =
    !isSelectedModuleOfferFlow &&
    !isSelectedModuleExpiredTrial &&
    hasSelectedModuleActivePlan &&
    visiblePlans?.length;
  const trialEndLabel = selectedModuleTrialEndsAt
    ? new Date(selectedModuleTrialEndsAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const moduleTagLabel = getModuleTagLabel({
    isOfferFlow:
      (!selectedModule?.isActive && !selectedModule?.isInTrial) ||
      isSelectedModuleInTrial ||
      isSelectedModuleExpiredTrial,
    moduleName: selectedModule?.moduleName,
    isExpiredTrial: isSelectedModuleExpiredTrial,
  });

  const isMonthlyOfferPeriod = selectedOfferPeriod !== BillingPeriods.YEARLY;
  const organisationAddonsRecurringSuffix =
    selectedPlanPeriod === BillingPeriods.YEARLY ? " /yr" : " /mo";

  const organisationAddonItems = organisationAddonRows;
  const receivablesAddonItems = useMemo(
    () => [...credits, ...additionalFeatures],
    [credits, additionalFeatures],
  );
  const allAddonItems = useMemo(
    () => [...receivablesAddonItems, ...organisationAddonItems],
    [receivablesAddonItems, organisationAddonItems],
  );

  useEffect(() => {
    if (!cancellingOrganisationAddonId || isLoadingCancelScheduled) return;

    const row = findAddonRowById(
      organisationAddonItems,
      cancellingOrganisationAddonId,
    );

    if (!row) {
      setCancellingOrganisationAddonId(null);
      return;
    }

    if (!isScheduledForRemoval(row)) {
      setCancellingOrganisationAddonId(null);
    }
  }, [
    cancellingOrganisationAddonId,
    isLoadingCancelScheduled,
    organisationAddonItems,
  ]);

  useEffect(() => {
    if (!pendingAddonState) return;

    const timeout = setTimeout(() => {
      setPendingAddonState(null);
    }, 12000);

    return () => clearTimeout(timeout);
  }, [pendingAddonState]);

  useEffect(() => {
    if (!pendingAddonState?.addonId) return;

    const matchedRow = findAddonRowById(
      allAddonItems,
      pendingAddonState.addonId,
    );
    const currentSignature = buildAddonRowSignature(matchedRow);

    if (currentSignature !== pendingAddonState.beforeSignature) {
      setPendingAddonState(null);
    }
  }, [pendingAddonState, allAddonItems]);

  const activeOrganisationUsage = organisationAddonItems.find(
    (addonItem) => addonItem.addonId === activeAddonId,
  )?.usage;
  const selectedModulePlan = useMemo(() => {
    if (!selectedModule) return null;

    return {
      title: selectedModule?.planName || selectedModule?.plan?.title,
      name: selectedModule?.planName || selectedModule?.plan?.name,
      amount: Number(
        selectedModule?.amountMinor || selectedModule?.plan?.amount || 0,
      ),
      period: selectedModule?.period || selectedModule?.plan?.period || null,
      nextBillingDate:
        selectedModule?.nextBillingDate ||
        selectedModule?.plan?.nextBillingDate ||
        plan?.nextBillingDate ||
        null,
      addons: selectedModule?.addons || selectedModule?.plan?.addons || {},
    };
  }, [selectedModule, plan?.nextBillingDate]);

  const addonModalPlan = useMemo(() => {
    if (selectedRailSection === ORGANISATION_MODULE_ID) {
      return {
        addons: {
          users: activeOrganisationUsage,
        },
        nextBillingDate: selectedModulePlan?.nextBillingDate,
      };
    }

    return selectedModulePlan;
  }, [activeOrganisationUsage, selectedModulePlan, selectedRailSection]);

  const activeOrganisationAddonCount = organisationAddonItems.filter(
    isConfiguredOrganisationAddon,
  ).length;

  const selectOrganisationAddons = useCallback(() => {
    setSelectedRailSection(ORGANISATION_MODULE_ID);
    selectModuleById(ORGANISATION_MODULE_ID);
  }, [selectModuleById]);

  const handleSelectPlan = useCallback(
    (planName, meta = {}) => {
      if (meta.isDowngrade) {
        showModal(<DowngradeModal />);
        return;
      }

      setTargetPlanName(planName);
    },
    [setTargetPlanName, showModal],
  );

  useEffect(() => {
    const isOrganisationDeepLink =
      selectedModuleFromQuery === ORGANISATION_MODULE_ID;

    if (!isOrganisationDeepLink) {
      hasBootstrappedOrgDeepLinkRef.current = false;
      return;
    }

    if (hasBootstrappedOrgDeepLinkRef.current) {
      return;
    }

    hasBootstrappedOrgDeepLinkRef.current = true;
    selectOrganisationAddons();
  }, [selectedModuleFromQuery, selectOrganisationAddons]);

  return (
    <Stack alignItems="center">
      <Shelf
        className={styles.page}
        width="100%"
        alignItems="flex-start"
        gap="large"
      >
        {isInitialLoading && <FullLayoutLoader />}

        <ModuleSummaryCard
          moduleTotalMinor={moduleTotalCurrentCadenceMinor}
          moduleTotalAnnualizedMinor={moduleTotalAnnualEquivalentMinor}
          formatCurrency={formatCurrency}
          currencyCode={currencyCode}
          activeModuleCount={activeModuleCount}
          activeOrganisationAddonCount={activeOrganisationAddonCount}
          moduleSubscriptions={moduleSubscriptions}
          availableModules={availableModules}
          isOrganisationAddonsSelected={
            selectedRailSection === ORGANISATION_MODULE_ID
          }
          onSelectOrganisationAddons={selectOrganisationAddons}
          selectedModule={selectedModule}
          selectedModuleMinor={selectedModuleAmountMinor}
          onSelectModule={(moduleName) => {
            setSelectedRailSection(getDefaultRailSection());
            selectModuleById(moduleName);
          }}
          onSelectLockedModule={(moduleName) => {
            setSelectedRailSection(getDefaultRailSection());
            selectModuleById(moduleName);
          }}
        />
        {selectedRailSection === ORGANISATION_MODULE_ID ? (
          <Card className={styles.modulePanel} p="none">
            <Stack gap="none">
              <Shelf
                justifyContent="space-between"
                alignItems="center"
                p="medium"
              >
                <Stack gap="small">
                  <span className={styles.moduleTag}>
                    <Users size={12} strokeWidth={2} />
                    <span className={styles.moduleTagLabel}>
                      ORGANISATION ADD-ONS
                    </span>
                  </span>
                  <Text fontSize="heading.1" fontWeight="bold">
                    All organisation add-ons
                  </Text>
                  <Text color="Mid Neutral 1" className={styles.moduleSubtitle}>
                    These apply across all modules.
                  </Text>
                </Stack>
              </Shelf>
              <div className={styles.moduleBody}>
                {isModuleSwitching ? (
                  <SkeletonLayout />
                ) : organisationAddonItems.length === 0 ? (
                  <div className={styles.organisationAddonsEmptyState}>
                    <Stack gap="small">
                      <Text
                        fontWeight="bold"
                        className={styles.emptyStateTitle}
                      >
                        No organisation add-ons available
                      </Text>
                      <Text
                        color="Mid Neutral 1"
                        className={styles.emptyStateBody}
                      >
                        We could not find any organisation add-ons for your
                        current subscription setup.
                      </Text>
                    </Stack>
                  </div>
                ) : (
                  <Stack gap="small" className={styles.organisationAddonsList}>
                    {!canPurchaseOrganisationAddons && (
                      <Banner intent="info" showCloseButton={false}>
                        Organisation add-ons can only be purchased when at least
                        one module has an active plan. This applies to current
                        and future add-ons in the organisations module.
                      </Banner>
                    )}
                    {organisationAddonItems.map((item) => {
                      const isUsersAddon =
                        item.addonId === addonIds.ADDITIONAL_USERS ||
                        item.addon?.id === addonIds.ADDITIONAL_USERS ||
                        item.addon?.id === addonIds.USER;
                      const isUnlimited =
                        Boolean(item.isUnlimitedUsersLimit) ||
                        Number(item.limit) >= 9999;
                      const showUsage = Number(item.limit) > 0 && !isUnlimited;
                      const usedCount = Number(item?.usage?.totalUsed || 0);
                      const usageLimit = Number(item?.limit || 0);
                      const isUsageOverLimit = Boolean(item?.isOverLimit);
                      const hasReachedUsageLimit =
                        !isUsageOverLimit &&
                        showUsage &&
                        usageLimit > 0 &&
                        usedCount >= usageLimit;
                      const isUsageLimitState =
                        isUsageOverLimit || hasReachedUsageLimit;
                      const usageProgressNow = isUsageOverLimit
                        ? 100
                        : Math.min(100, Number(item?.percentage || 0));
                      const usageStatusLabel = isUsageOverLimit
                        ? "Over limit"
                        : hasReachedUsageLimit
                          ? "Reached limit"
                          : `${usageProgressNow}%`;
                      const isAddonScheduledForRemoval =
                        isScheduledForRemoval(item);
                      const freeAllowance = Number(item.freeAllowance || 0);
                      const purchasedAllowance = Number(
                        item.purchasedAllowance || 0,
                      );
                      const shouldShowPurchasedUsersRow = !(
                        purchasedAllowance === 0 && isUnlimited
                      );
                      const includedUsersByPlan = Array.isArray(
                        item.includedUsersByPlan,
                      )
                        ? item.includedUsersByPlan
                        : [];
                      const disablePurchaseMore =
                        !canPurchaseOrganisationAddons ||
                        isAddonScheduledForRemoval;
                      const disableCardClick =
                        disablePurchaseMore || isUsersAddon;
                      const isCancellingThisAddon =
                        cancellingOrganisationAddonId === item.addonId;
                      const itemAmountMinor =
                        Number(item.rowAmountMinor) ||
                        Number(
                          item.addon?.price ||
                            item.addon?.tiers?.[0]?.price ||
                            0,
                        );
                      const unitPriceMinor = Number(
                        item.usage?.price ||
                          item.addon?.price ||
                          item.addon?.tiers?.[0]?.price ||
                          0,
                      );

                      return (
                        <Card
                          key={item.id}
                          p="small"
                          className={`${styles.addonRow} ${!disableCardClick ? styles.addonRowInteractive : ""} ${!item.isActive ? styles.notSubscribed : ""} ${styles.featureAddonRow} ${showUsage || isUsersAddon ? styles.featureAddonRowWithUsage : ""} ${removingAddonId === item.addonId || isCancellingThisAddon ? styles.loading : ""}`}
                          onClick={
                            disableCardClick
                              ? undefined
                              : () => onActionClick(item)
                          }
                          onKeyDown={(event) => {
                            if (disableCardClick) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onActionClick(item);
                            }
                          }}
                          role={disableCardClick ? undefined : "button"}
                          tabIndex={disableCardClick ? -1 : 0}
                        >
                          <Shelf
                            justifyContent="space-between"
                            alignItems="stretch"
                            gap="medium"
                            className={styles.rowContent}
                          >
                            <Stack className={styles.rowLeft} gap="small">
                              <Shelf
                                alignItems="center"
                                justifyContent="space-between"
                                gap="small"
                              >
                                <Shelf alignItems="center" gap="xsmall">
                                  <span
                                    className={
                                      styles.organisationAddonsItemIcon
                                    }
                                  >
                                    <Users
                                      size={14}
                                      color="#7C8A9C"
                                      strokeWidth={2}
                                    />
                                  </span>
                                  <Text
                                    fontWeight="bold"
                                    className={
                                      styles.organisationAddonsItemName
                                    }
                                  >
                                    {item.name}
                                  </Text>
                                </Shelf>
                              </Shelf>

                              <Stack
                                gap="xsmall"
                                className={styles.organisationAddonsItemMeta}
                              >
                                {showUsage && (
                                  <Stack
                                    gap="xsmall"
                                    className={styles.usageMeterWrap}
                                  >
                                    <Shelf
                                      justifyContent="space-between"
                                      className={styles.usageRow}
                                    >
                                      <Text
                                        color={
                                          isUsageLimitState
                                            ? "Red"
                                            : "Mid Neutral 1"
                                        }
                                        className={
                                          isUsageLimitState
                                            ? styles.overLimitUsageText
                                            : ""
                                        }
                                      >
                                        <strong
                                          className={`${styles.usageValue} ${
                                            isUsageLimitState
                                              ? styles.overLimitUsageValue
                                              : ""
                                          }`}
                                        >
                                          {usedCount}
                                        </strong>{" "}
                                        of{" "}
                                        <strong
                                          className={`${styles.usageValue} ${
                                            isUsageLimitState
                                              ? styles.overLimitUsageValue
                                              : ""
                                          }`}
                                        >
                                          {usageLimit}
                                        </strong>{" "}
                                        used
                                      </Text>
                                      <Text
                                        color={
                                          isUsageLimitState
                                            ? "Red"
                                            : "Mid Neutral 1"
                                        }
                                      >
                                        {usageStatusLabel}
                                      </Text>
                                    </Shelf>
                                    <div className={styles.usageBarWrap}>
                                      <Progress
                                        now={usageProgressNow}
                                        min={0}
                                        max={100}
                                        height="8px"
                                        color={
                                          isUsageLimitState ? "Red" : "Orange"
                                        }
                                        className={styles.usageBar}
                                      />
                                    </div>
                                  </Stack>
                                )}

                                {includedUsersByPlan.length > 0 && (
                                  <div className={styles.includedByPlanTopRow}>
                                    <Popover
                                      shown={includedByPlanOpenId === item.id}
                                      onToggle={(shown) => {
                                        setIncludedByPlanOpenId(
                                          shown ? item.id : null,
                                        );
                                      }}
                                      position={Position.BOTTOM_RIGHT}
                                      contentClassName={
                                        styles.includedByPlanPopover
                                      }
                                      target={
                                        <Button
                                          appearance="minimal"
                                          size="narrow"
                                          active={
                                            includedByPlanOpenId === item.id
                                          }
                                          className={
                                            styles.includedByPlanTriggerAbove
                                          }
                                        >
                                          Breakdown by plan
                                        </Button>
                                      }
                                    >
                                      <IncludedByPlanTable
                                        includedUsersByPlan={
                                          includedUsersByPlan
                                        }
                                      />
                                    </Popover>
                                  </div>
                                )}

                                <div className={styles.userAllowanceGrid}>
                                  {shouldShowPurchasedUsersRow && (
                                    <div className={styles.allowanceStatCard}>
                                      <div className={styles.allowanceStatRow}>
                                        <span
                                          className={styles.allowanceStatTitle}
                                        >
                                          <UserPlus
                                            size={14}
                                            color="#6A7B8F"
                                            strokeWidth={2}
                                          />
                                          <Text
                                            className={
                                              styles.allowanceStatLabel
                                            }
                                          >
                                            Paid users
                                          </Text>
                                        </span>
                                        <Text
                                          className={styles.allowanceStatValue}
                                        >
                                          {purchasedAllowance}
                                        </Text>
                                      </div>
                                    </div>
                                  )}

                                  <div
                                    className={`${styles.allowanceStatCard} ${isUnlimited ? styles.allowanceStatCardPositive : ""}`}
                                  >
                                    <div className={styles.allowanceStatRow}>
                                      <span
                                        className={styles.allowanceStatTitle}
                                      >
                                        <Gift
                                          size={14}
                                          color="#5F8B6F"
                                          strokeWidth={2}
                                        />
                                        <Text
                                          className={styles.allowanceStatLabel}
                                        >
                                          Free users
                                        </Text>
                                      </span>
                                      <Text
                                        className={styles.allowanceStatValue}
                                      >
                                        {isUnlimited
                                          ? "Unlimited"
                                          : freeAllowance}
                                      </Text>
                                    </div>
                                  </div>

                                  {isUnlimited && (
                                    <div
                                      className={`${styles.allowanceStatCard} ${styles.allowanceStatCardMuted}`}
                                    >
                                      <div className={styles.allowanceStatRow}>
                                        <Text
                                          className={styles.allowanceStatLabel}
                                        >
                                          Current usage
                                        </Text>
                                        <Text
                                          className={styles.allowanceStatValue}
                                        >
                                          {item.usage?.totalUsed ?? 0}
                                        </Text>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </Stack>

                              {isAddonScheduledForRemoval && (
                                <Stack
                                  gap="small"
                                  className={styles.scheduledRemovalNotice}
                                  py="xxsmall"
                                  px="small"
                                >
                                  <Shelf
                                    gap="small"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Text
                                      color="Red"
                                      fontSize="small"
                                      fontWeight="bold"
                                    >
                                      × Scheduled for removal
                                    </Text>
                                    <Button
                                      appearance="minimal"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setCancellingOrganisationAddonId(
                                          item.addonId,
                                        );
                                        onCancelScheduledChange(item);
                                      }}
                                      disabled={
                                        isLoadingCancelScheduled ||
                                        isCancellingThisAddon
                                      }
                                      data-trackingid={`organisation-addon-cancel-removal-${item.addonId}`}
                                    >
                                      Cancel
                                    </Button>
                                  </Shelf>
                                </Stack>
                              )}
                            </Stack>

                            {!isUnlimited && (
                              <Stack
                                className={styles.rowRight}
                                alignItems="flex-end"
                                gap="small"
                              >
                                <Text
                                  fontWeight="bold"
                                  className={styles.rowAmount}
                                >
                                  {formatCurrency(
                                    itemAmountMinor / 100,
                                    currencyCode,
                                  )}
                                  {organisationAddonsRecurringSuffix}
                                </Text>
                                <Text
                                  color="Mid Neutral 1"
                                  className={styles.rowMeta}
                                >
                                  {unitPriceMinor > 0
                                    ? `${formatCurrency(unitPriceMinor / 100, currencyCode)} per unit`
                                    : "Per unit"}
                                </Text>
                                <Stack gap="xsmall">
                                  {!isAddonScheduledForRemoval && (
                                    <Button
                                      appearance="secondary"
                                      size="narrow"
                                      className={`${styles.actionButtonBase} ${styles.purchaseMoreButton}`}
                                      disabled={disablePurchaseMore}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (!disablePurchaseMore) {
                                          onActionClick(item);
                                        }
                                      }}
                                    >
                                      Purchase more
                                    </Button>
                                  )}
                                  {(purchasedAllowance > 0 ||
                                    Number(item.rowAmountMinor || 0) > 0) &&
                                    !isAddonScheduledForRemoval && (
                                      <Button
                                        appearance="secondary"
                                        size="narrow"
                                        className={`${styles.actionButtonBase} ${styles.removeCta}`}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onRemoveClick(item);
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                </Stack>
                              </Stack>
                            )}
                          </Shelf>
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </div>
            </Stack>
          </Card>
        ) : (
          selectedModule && (
            <Card
              key={selectedModule?.id || "module-panel"}
              className={styles.modulePanel}
              p="none"
            >
              <Stack gap="none">
                <Shelf
                  justifyContent="space-between"
                  alignItems="center"
                  p="medium"
                >
                  <Stack gap="small">
                    <span className={styles.moduleTag}>
                      <FileText size={12} strokeWidth={2} />
                      <span className={styles.moduleTagLabel}>
                        {moduleTagLabel}
                      </span>
                    </span>
                    <Text fontSize="heading.1" fontWeight="bold">
                      {selectedModule?.moduleName || "Add a module"}
                    </Text>
                    <Text
                      color="Mid Neutral 1"
                      className={styles.moduleSubtitle}
                    >
                      {selectedModule
                        ? selectedPlanRevenueText || ""
                        : "Choose a module to unlock its subscription and add-ons."}
                    </Text>
                  </Stack>
                  <Stack alignItems="flex-end" gap="small">
                    {!isSelectedModuleOfferFlow && (
                      <Text
                        fontSize="heading.2"
                        fontWeight="bold"
                        className={styles.planPrice}
                      >
                        {formatCurrency(
                          selectedModuleAmountMinor / 100,
                          currencyCode,
                        )}
                        <span className={styles.planPriceSuffix}>
                          {selectedPlanRecurringSuffix}
                        </span>
                      </Text>
                    )}
                    {canComparePlans && (
                      <ComparePlansPopover
                        showComparePlans={showComparePlans}
                        setShowComparePlans={setShowComparePlans}
                        loadingSubscription={loadingSubscription}
                        visiblePlans={visiblePlans}
                        currentPlanAmount={
                          Number.isFinite(compareCurrentPlanAmount)
                            ? compareCurrentPlanAmount
                            : currentPlanAmount
                        }
                        recurringSuffix={comparisonRecurringSuffix}
                        selectedOfferPeriod={selectedOfferPeriod}
                        setSelectedOfferPeriod={setSelectedOfferPeriod}
                        onSelectPlan={handleSelectPlan}
                        currencyCode={currencyCode}
                        disabled={!visiblePlans.length}
                      />
                    )}
                  </Stack>
                </Shelf>
                <div className={styles.moduleBody}>
                  {isModuleSwitching ? (
                    <SkeletonLayout />
                  ) : selectedModule &&
                    !selectedModule?.isActive &&
                    !selectedModule?.isInTrial &&
                    !isSelectedModuleExpiredTrial ? (
                    <Stack gap="medium">
                      <Text
                        color="Mid Neutral 1"
                        className={styles.moduleSubtitle}
                      >
                        Start a 3-day trial to unlock this module. After your
                        trial starts, you can choose the plan you want for
                        checkout.
                      </Text>
                      <Shelf>
                        <Button
                          appearance="primary"
                          size="narrow"
                          loading={isStartingModuleTrial}
                          onClick={startSelectedModuleTrial}
                        >
                          Start a trial
                        </Button>
                      </Shelf>
                    </Stack>
                  ) : selectedModule && isSelectedModuleInTrial ? (
                    <Stack gap="medium">
                      <Text
                        color="Mid Neutral 1"
                        className={styles.moduleSubtitle}
                      >
                        {trialEndLabel
                          ? `Trial ends on ${trialEndLabel}. Subscribe now to keep full access without interruption.`
                          : "This module is in trial. Subscribe now to keep full access without interruption."}
                      </Text>
                      <Shelf
                        alignItems="center"
                        gap="small"
                        className={styles.modulePlanPeriodToggle}
                      >
                        <Text>Monthly</Text>
                        <Toggle
                          toggleOn={!isMonthlyOfferPeriod}
                          handleToggle={() =>
                            setSelectedOfferPeriod(
                              isMonthlyOfferPeriod
                                ? BillingPeriods.YEARLY
                                : BillingPeriods.MONTHLY,
                            )
                          }
                        />
                        <Text>Annual</Text>
                      </Shelf>
                      {visiblePlans?.length ? (
                        <PlanComparisonGrid
                          visiblePlans={visiblePlans}
                          currentPlanAmount={
                            Number.isFinite(compareCurrentPlanAmount)
                              ? compareCurrentPlanAmount
                              : currentPlanAmount
                          }
                          recurringSuffix={comparisonRecurringSuffix}
                          onSelectPlan={handleSelectPlan}
                          actionMode="subscribe"
                          subscribeCtaLabel="Start a subscription"
                          currencyCode={currencyCode}
                        />
                      ) : (
                        <Text
                          color="Mid Neutral 1"
                          className={styles.moduleSubtitle}
                        >
                          Plan offers are temporarily unavailable for this
                          module.
                        </Text>
                      )}
                    </Stack>
                  ) : selectedModule && isSelectedModuleExpiredTrial ? (
                    <Stack gap="medium">
                      <Text
                        color="Mid Neutral 1"
                        className={styles.moduleSubtitle}
                      >
                        Your trial has expired. Choose a plan to continue using
                        this module.
                      </Text>
                      <Shelf
                        alignItems="center"
                        gap="small"
                        className={styles.modulePlanPeriodToggle}
                      >
                        <Text>Monthly</Text>
                        <Toggle
                          toggleOn={!isMonthlyOfferPeriod}
                          handleToggle={() =>
                            setSelectedOfferPeriod(
                              isMonthlyOfferPeriod
                                ? BillingPeriods.YEARLY
                                : BillingPeriods.MONTHLY,
                            )
                          }
                        />
                        <Text>Annual</Text>
                      </Shelf>
                      {visiblePlans?.length ? (
                        <PlanComparisonGrid
                          visiblePlans={visiblePlans}
                          currentPlanAmount={
                            Number.isFinite(compareCurrentPlanAmount)
                              ? compareCurrentPlanAmount
                              : currentPlanAmount
                          }
                          recurringSuffix={comparisonRecurringSuffix}
                          onSelectPlan={handleSelectPlan}
                          actionMode="subscribe"
                          subscribeCtaLabel="Subscribe now"
                          currencyCode={currencyCode}
                        />
                      ) : (
                        <Text
                          color="Mid Neutral 1"
                          className={styles.moduleSubtitle}
                        >
                          Plan offers are temporarily unavailable for this
                          module.
                        </Text>
                      )}
                    </Stack>
                  ) : selectedModule && selectedModuleSupportsAddons ? (
                    <Stack gap="large" pt="small">
                      <AddonRowsSection
                        title="CREDITS"
                        rows={credits}
                        sectionType="credits"
                        billingTerm={selectedPlanPeriod}
                        currencyCode={currencyCode}
                        onActionClick={onActionClick}
                        onRemoveClick={onRemoveClick}
                        removingAddonId={removingAddonId}
                        pendingAddonId={pendingAddonId}
                        onCancelScheduledChange={onCancelScheduledChange}
                        isLoadingCancelScheduled={isLoadingCancelScheduled}
                      />
                      <AddonRowsSection
                        title="ADDITIONAL FEATURES"
                        rows={additionalFeatures}
                        sectionType="additional"
                        billingTerm={selectedPlanPeriod}
                        currencyCode={currencyCode}
                        onActionClick={onActionClick}
                        onRemoveClick={onRemoveClick}
                        removingAddonId={removingAddonId}
                        pendingAddonId={pendingAddonId}
                        onCancelScheduledChange={onCancelScheduledChange}
                        isLoadingCancelScheduled={isLoadingCancelScheduled}
                      />
                    </Stack>
                  ) : selectedModule ? (
                    <Text
                      color="Mid Neutral 1"
                      className={styles.moduleSubtitle}
                    >
                      No add-ons are currently available for this module.
                    </Text>
                  ) : (
                    <Text
                      color="Mid Neutral 1"
                      className={styles.moduleSubtitle}
                    >
                      No active module subscriptions yet.
                    </Text>
                  )}

                  {!isSelectedModuleOfferFlow && (
                    <Shelf
                      justifyContent="space-between"
                      alignItems="center"
                      pt="medium"
                      pb="medium"
                      className={styles.panelTotalRow}
                    >
                      <Text
                        color="Mid Neutral 1"
                        className={styles.panelTotalLabel}
                      >
                        {selectedModule?.moduleName || "Module"} plan{" "}
                        {selectedPlanPeriod === "year" ? "annual" : "monthly"}{" "}
                        total
                      </Text>
                      <Text
                        fontSize="heading.2"
                        fontWeight="bold"
                        className={styles.panelTotalValue}
                      >
                        {formatCurrency(
                          selectedModuleAmountMinor / 100,
                          currencyCode,
                        )}
                        <Text fontSize="small" color="Mid Neutral 1">
                          {" "}
                          {selectedPlanRecurringSuffix}
                        </Text>
                      </Text>
                    </Shelf>
                  )}
                </div>
              </Stack>
            </Card>
          )
        )}
      </Shelf>

      {activeAddonId && (
        <AddAddonModal
          addonId={activeAddonId}
          moduleName={
            selectedRailSection === ORGANISATION_MODULE_ID
              ? ORGANISATION_MODULE_ID
              : selectedModule?.id
          }
          plan={addonModalPlan}
          open
          close={() => setActiveAddonId(null)}
          onSuccess={async (requestPayload) => {
            const normalizedRequestedAddonId = normalizePendingAddonId(
              requestPayload?.addonId,
            );
            const nextPendingAddonId =
              activeAddonId || normalizedRequestedAddonId || null;

            const pendingRow = findAddonRowById(
              allAddonItems,
              nextPendingAddonId,
            );

            setPendingAddonState({
              addonId: nextPendingAddonId,
              beforeSignature: buildAddonRowSignature(pendingRow),
            });

            try {
              await Promise.all([
                refetchSubscriptionData(),
                refetchSelectedModuleAddons?.(),
                selectedRailSection === ORGANISATION_MODULE_ID
                  ? refetchOrganisationAddons()
                  : Promise.resolve(),
              ]);
            } finally {
              setActiveAddonId(null);
            }
          }}
        />
      )}
    </Stack>
  );
};

export default Addons;
